<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { browser } from '$app/environment';
  import { page } from '$app/stores';
  import { themeStore } from '$lib/stores/themeStore';
  import { CONTENT_TYPES, HEADER_VARIANT } from '$lib/constants/constants';
  import type { HeaderNavigationProps, HeaderCtaProps } from '../../../types/layoutTypes';
  import SweatLogo from '../../../../assets/svgs/sweat-logo.svelte';
  import HamburgerIcon from '../../../../assets/svgs/hamburger.svelte';
  import CloseIcon from '../../../../assets/svgs/close.svelte';
  import MobileNav from './NavMobile.svelte';
  import { ensureOptimizely, getVariationItem, optimizelyStore } from '$lib/stores/optimizelyStore';
  import type { VariationContainerProps } from '$lib/types/contentTypes';

  export let headerNavigation: HeaderNavigationProps | undefined;
  export let headerCta: HeaderCtaProps | VariationContainerProps<HeaderCtaProps> | undefined;

  export let isMobileMenuOpen = false;

  let theme;
  // Subscribe to changes in the store
  const unsubscribe = themeStore.subscribe((headerTheme) => {
    theme = headerTheme;
  });

  onDestroy(unsubscribe);

  const handleMobileMenu = () => {
    isMobileMenuOpen = !isMobileMenuOpen;
  };

  const closeMobileMenu = () => {
    isMobileMenuOpen = false;
    enableBodyScrolling();
  };

  const disableBodyScrolling = () => {
    document.body.style.overflow = 'hidden';
  };

  const enableBodyScrolling = () => {
    if (browser) {
      document.body.style.overflow = 'visible';
    }
  };

  $: isMobileMenuOpen && disableBodyScrolling();
  $: !isMobileMenuOpen && enableBodyScrolling();
  $: $page.url.href && closeMobileMenu();

  $: triangleBorderBottom =
    theme.headerVariant === HEADER_VARIANT.DEFAULT
      ? 'before:border-b-gray-900 border-gray-900'
      : 'before:border-b-white border-white';

  const borderTopfirstsubNavigation = `border-t-2 border-white`;
  const tooltipTriangleClass = `before:border-8 before:border-solid before:absolute before:-top-[17px] before:left-4`;

  let path;
  $: path = $page.url.pathname;

  const headerCtaHasVariationContainer =
    (headerCta as VariationContainerProps<HeaderCtaProps>)?.contentType ===
    CONTENT_TYPES.VARIATION_CONTAINER;

  let headerCtaVariationItem: VariationContainerProps<HeaderCtaProps>['variationsCollection']['items'][0];

  onMount(async () => {
    if (headerCtaHasVariationContainer && !$page.url.href.includes('join')) {
      if (!$optimizelyStore.isReady) {
        await ensureOptimizely();
      }

      const variationItem = getVariationItem(headerCta as VariationContainerProps<HeaderCtaProps>);

      if (variationItem) {
        headerCtaVariationItem = variationItem;
      }
    }
  });
</script>

<nav
  class="flex relative items-center justify-between lg:justify-center pl-4 pr-3 sm:px-8 h-[4.5rem] sm:h-[5.875rem] {theme.headerBackground}"
>
  <a href="/" class="flex flex-row items-center" aria-label="Home page link">
    <SweatLogo colour={theme.headerVariant === HEADER_VARIANT.DEFAULT ? '#FF1A66' : '#FFFFFF'} />
  </a>
  {#if $page.url.href.includes('blogs')}
    <p class="text-[1.5625rem] text-primary-200 pl-[0.5rem] hidden sm:block absolute left-[8.5rem]">
      Blog
    </p>
  {/if}

  <div class="w-full hidden h-full lg:flex flex-grow md:justify-center lg:w-auto">
    <ul role="list" class="grid gap-8 grid-flow-col">
      {#if headerNavigation?.length}
        {#each headerNavigation as navigationItem}
          <li class="flex flex-col justify-center group relative">
            <a
              href={navigationItem.url}
              class="{theme.headerText} group-hover:{theme.headerText} text-md font-semibold {path.startsWith(
                navigationItem.url,
              )
                ? '!font-bold'
                : ''}"
            >
              {navigationItem.title}
            </a>
            {#if navigationItem?.subNavigationItemsCollection?.items.length}
              <ul
                class="flex flex-col bg-white w-[148px] invisible group-hover:visible
            absolute top-[5.9rem] {borderTopfirstsubNavigation} {tooltipTriangleClass} {triangleBorderBottom} {theme.headerTriangle}"
              >
                {#each navigationItem.subNavigationItemsCollection.items as subNavigationItem}
                  <li class="md:px-4 md:py-2">
                    <a class=" " href={subNavigationItem.url}>
                      <span
                        class="text-gray-700 hover:text-gray-950 text-sm font-medium {path.startsWith(
                          subNavigationItem.url,
                        )
                          ? '!font-bold'
                          : ''}"
                        >{subNavigationItem.title}
                      </span>
                    </a>
                  </li>
                {/each}
              </ul>
            {/if}
          </li>
        {/each}
      {/if}
    </ul>
  </div>
  <div class="flex items-center min-w-[120px]">
    {#if !isMobileMenuOpen && !$page.url.href.includes('join')}
      {#if !headerCtaHasVariationContainer || (headerCtaHasVariationContainer && $optimizelyStore.isReady)}
        <a
          href={(headerCtaVariationItem || headerCta)?.url}
          class="flex py-2 px-6 sm:py-2.5 sm:px-8 text-sm sm:text-md hover:bg-primary-300 font-semibold border-primary-500 border-full rounded-full plausible-event-name=nav_CTA_{(
            headerCtaVariationItem || headerCta
          )?.title
            ?.split(' ')
            .join('_')}"
          style="background:{theme.headerCtaBackground}; color:{theme.headerCtaText};"
          >{(headerCtaVariationItem || headerCta)?.title}
        </a>
      {/if}
    {/if}
    <button
      on:click={handleMobileMenu}
      class="flex ml-4 p-2 gray-500 lg:hidden"
      aria-label={isMobileMenuOpen ? 'close menu button' : 'open menu button'}
    >
      <svelte:component
        this={isMobileMenuOpen ? CloseIcon : HamburgerIcon}
        color={theme.headerMenuIconColor}
      />
    </button>
  </div>
  {#if !headerCtaHasVariationContainer || (headerCtaHasVariationContainer && $optimizelyStore.isReady)}
    <MobileNav
      ctaBackgroundColor="#FF1A66"
      ctaTextColor="#FFF"
      ctaTitle={(headerCtaVariationItem || headerCta)?.title}
      ctaUrl={(headerCtaVariationItem || headerCta)?.url}
      navigation={headerNavigation}
      isVisible={isMobileMenuOpen}
      {handleMobileMenu}
    />
  {/if}
</nav>
